import { useState, useCallback, useMemo, useRef } from 'react';
import { trpc } from '../../lib/trpc';
import { MediaUpload } from './MediaUpload';
import { MediaPickerOrderedStrip } from './MediaPickerOrderedStrip';
import { MediaPickerTile } from './MediaPickerTile';
import type { Media, EntityType } from '@wineryxplorer/shared';
import { Button } from '../ui/Button';
import type { DragState } from './MediaPickerOrderedStrip';

export interface MediaPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'single' | 'multi' | 'multi-ordered';
  venueId: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onSave?: () => void;
  title: string;
  maxSelection?: number;
  showUpload?: boolean;
  className?: string;
  isSaving?: boolean;
}

export const MediaPickerModal = ({
  isOpen,
  onClose,
  mode,
  venueId,
  selectedIds,
  onSelectionChange,
  onSave,
  title,
  maxSelection = mode === 'single' ? 1 : 5,
  showUpload = true,
  className = '',
  isSaving = false,
}: MediaPickerModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedIndex: null,
    dragOverIndex: null,
  });
  
  const dragItemRef = useRef<HTMLDivElement>(null);

  // Fetch venue media
  const { data: mediaLibrary, isPending, refetch } = trpc.media.getMediaLibrary.useQuery({
    entityType: 'venue' as EntityType,
    entityId: venueId,
    limit: 100,
  }, {
    enabled: isOpen,
  });

  // Filter media based on search query
  const filteredMedia = useMemo(() => {
    if (!mediaLibrary?.media) return [];
    
    const mediaList = mediaLibrary.media as unknown as Media[];
    return mediaList.filter((media) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        media.filename.toLowerCase().includes(query) ||
        media.caption?.toLowerCase().includes(query) ||
        media.tags.some((tag: string) => tag.toLowerCase().includes(query))
      );
    });
  }, [mediaLibrary?.media, searchQuery]);

  // Get selected media in order for multi-ordered mode
  const selectedMediaInOrder = useMemo(() => {
    if (mode !== 'multi-ordered') return [];
    
    return selectedIds
      .map(id => filteredMedia.find((media) => media.id === id))
      .filter((media): media is Media => media !== undefined);
  }, [selectedIds, filteredMedia, mode]);

  // Handle media selection
  const handleMediaSelect = useCallback((media: Media) => {
    if (mode === 'single') {
      onSelectionChange([media.id]);
    } else {
      const isSelected = selectedIds.includes(media.id);
      
      if (isSelected) {
        // Remove from selection
        onSelectionChange(selectedIds.filter(id => id !== media.id));
      } else {
        // Add to selection if under limit
        if (selectedIds.length < maxSelection) {
          onSelectionChange([...selectedIds, media.id]);
        }
      }
    }
  }, [mode, selectedIds, onSelectionChange, maxSelection]);

  // Handle upload success
  const handleUploadSuccess = useCallback(() => {
    refetch();
    setShowUploadArea(false);
  }, [refetch]);

  // Drag and drop handlers for multi-ordered mode
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    if (mode !== 'multi-ordered') return;
    
    setDragState({
      isDragging: true,
      draggedIndex: index,
      dragOverIndex: null,
    });
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  }, [mode]);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    if (mode !== 'multi-ordered' || !dragState.isDragging) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    setDragState(prev => ({
      ...prev,
      dragOverIndex: index,
    }));
  }, [mode, dragState.isDragging]);

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedIndex: null,
      dragOverIndex: null,
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (mode !== 'multi-ordered' || dragState.draggedIndex === null) return;
    
    const dragIndex = dragState.draggedIndex;
    if (dragIndex === dropIndex) return;
    
    const newOrder = [...selectedIds];
    const draggedId = newOrder[dragIndex];
    
    // Remove dragged item
    newOrder.splice(dragIndex, 1);
    
    // Insert at new position
    const insertIndex = dragIndex < dropIndex ? dropIndex - 1 : dropIndex;
    newOrder.splice(insertIndex, 0, draggedId);
    
    onSelectionChange(newOrder);
    handleDragEnd();
  }, [mode, dragState.draggedIndex, selectedIds, onSelectionChange, handleDragEnd]);

  // Handle save and close
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
    } else {
      onClose();
    }
  }, [onSave, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${className}`}
      onClick={handleBackdropClick}
    >
      <div className="bg-gray-50 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {mode === 'single' && 'Select one image'}
                {mode === 'multi' && `Select up to ${maxSelection} images`}
                {mode === 'multi-ordered' && `Select and order up to ${maxSelection} images`}
                {selectedIds.length > 0 && ` • ${selectedIds.length} selected`}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {showUpload && (
                <button
                  onClick={() => setShowUploadArea(!showUploadArea)}
                  className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100"
                >
                  {showUploadArea ? 'Hide Upload' : 'Upload New'}
                </button>
              )}
              
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Upload Area */}
          {showUploadArea && showUpload && (
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <MediaUpload
                entityType="venue"
                entityId={venueId}
                onUploadSuccess={handleUploadSuccess}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4"
              />
            </div>
          )}

          {/* Search */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Search images..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500"
              />
              <svg
                className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Selected Images (for multi-ordered mode) */}
          {mode === 'multi-ordered' && selectedMediaInOrder.length > 0 && (
            <MediaPickerOrderedStrip
              media={selectedMediaInOrder}
              dragState={dragState}
              dragItemRef={dragItemRef}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onRemove={handleMediaSelect}
            />
          )}

          {/* Media Grid */}
          <div className="flex-1 overflow-auto p-6">
            {isPending ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <span className="ml-3 text-gray-600">Loading images...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Media Grid with enhanced cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredMedia.map((media: Media) => (
                    <MediaPickerTile
                      key={media.id}
                      media={media}
                      mode={mode}
                      isSelected={selectedIds.includes(media.id)}
                      isAtSelectionLimit={selectedIds.length >= maxSelection}
                      selectionIndex={
                        mode === 'multi-ordered' ? selectedIds.indexOf(media.id) : -1
                      }
                      onSelect={handleMediaSelect}
                    />
                  ))}
                </div>

                {/* Empty state */}
                {filteredMedia.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <svg
                      className="w-16 h-16 text-gray-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-gray-500 text-lg font-medium">
                      {searchQuery ? 'No images match your search' : 'No images found'}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {searchQuery ? 'Try a different search term' : 'Upload some images to get started'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
          <div className="text-sm text-gray-600 font-sans">
            {selectedIds.length > 0 && (
              <span>
                {selectedIds.length} of {maxSelection} selected
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={(mode === 'single' && selectedIds.length === 0) || isSaving}
              loading={isSaving}
            >
              {isSaving ? 'Saving...' : (mode === 'single' ? 'Select' : 'Save Selection')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};